import { z } from 'zod';
import { FocusArea } from 'libs/end-to-end/src/focus-area';

export const NumericDisplayUnits = ['dollars', 'percentage', 'count'] as const;

export const NumericDisplayUnit = z
  .enum(NumericDisplayUnits)
  .describe('NumericDisplayUnit');

export type NumericDisplayUnit = z.infer<typeof NumericDisplayUnit>;

export const MetricDisplayModifier = z
  .enum([
    'Average',
    'LogAverage',
    'Growth',
    'Joining',
    'LogJoining',
    'Leaving',
    'Log',
    'RunningAverage',
    'Variance',
  ])
  .describe('MetricDisplayModifier');

export type MetricDisplayModifier = z.infer<typeof MetricDisplayModifier>;

export const MetricDisplayModifiers = Object.values(
  MetricDisplayModifier.Values,
);

/**
 * Attempts to determine the unit of a metric based on its characteristics.
 *
 * @param knownMaximum - The known maximum value for the metric, or null if unknown.
 * @param isMetricBoolean - Whether the metric is a boolean (true/false) type.
 * @param metricFocusArea - The focus area of the metric, which can influence its unit.
 * @param definitionUnit - An optional predefined unit for the metric.
 * @returns 'dollars' | 'percentage' | 'count'
 */
export const determineUnitOfMetric = ({
  knownMaximum,
  isMetricBoolean,
  metricFocusArea,
  definitionUnit,
}: {
  knownMaximum: number | null;
  isMetricBoolean: boolean;
  metricFocusArea: FocusArea;
  definitionUnit: NumericDisplayUnit | undefined;
}): NumericDisplayUnit => {
  if (definitionUnit) {
    return definitionUnit;
  }

  if (isMetricBoolean) {
    return 'percentage';
  }

  if (
    metricFocusArea === 'Compensation' &&
    (knownMaximum === null || knownMaximum > 5)
  ) {
    return 'dollars';
  }

  return 'count';
};
